/**
 * SHORT-FORM composition (Reels/TikTok/Shorts) — porte do edvid
 * (~/.claude/skills/edvid/assets/shortform/src/Main.tsx), DATA-DRIVEN.
 *
 * Diferenças de porte (e SÓ estas):
 *  - os JSONs de public/ (captions, track, segments, edit-data, caption-cues) viram props
 *    (ReelsProps) lidas por useReels();
 *  - staticFile() vira `${assetBase}/sfx/<arquivo>` para SFX e URL absoluta para mídia;
 *  - hook realce/misto pintam hook.accent (o edvid tinha #ff5200 fixo); stacked usa captions.accent;
 *  - hook.enabled=false / captions.enabled=false não desenham a camada; captions.style ausente = karaoke;
 *  - "behind" (atrás da pessoa): Img/matte por URL; no @remotion/player o matte usa mattePreview (WebM
 *    VP9 com alfa) porque o <video> do Chrome não decodifica ProRes;
 *  - camada de texto só monta com as fontes carregadas (cache do measureText, ver fonts.ts);
 *  - segmentos vazios = um segmento só; camera.tracking=false ignora o track.
 * Constantes, easing, fit, janelas por frame, VIDEO_LAG e REGRA DA CAPA idênticos.
 *
 * Audio: keep layers low (whoosh ~0.09, pop ~0.12, music ~0.0445) and always run
 * a final loudnorm pass on the render — voice + music + SFX summed will clip.
 */
import React, { useMemo } from 'react';
import {
  AbsoluteFill,
  Audio,
  Img,
  OffthreadVideo,
  Sequence,
  interpolate,
  Easing,
  getRemotionEnvironment,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';
import { measureText } from '@remotion/layout-utils';
import { PLAYFAIR, POPPINS as fontFamily, useFontsReady } from './fonts';
import { DEFAULT_ASSET_BASE, ReelsProvider, Sfx, assetUrl, clamp, clamp01, useReels } from './context';
import type { ReelsData } from './context';
import { CustomGraphics, useVideoLag } from './CustomGraphics';
import { StackedCaptions } from './StackedCaptions';
import { ScatterCaptions } from './ScatterCaptions';
import { SimpleCaptions, SIMPLE_VARIANTS } from './SimpleCaptions';
import type { BehindItem, ReelsProps } from './types';

type Caption = { text: string; startMs: number; endMs: number };

// ============ DYNAMIC CAMERA (hard zoom on cuts + push-in + eye tracking) ======
export const DynamicVideo: React.FC<{ src?: string; frameOffset?: number; transparent?: boolean; children?: React.ReactNode }> = ({
  src,
  frameOffset = 0,
  transparent = false,
  children,
}) => {
  const frame = useCurrentFrame() + frameOffset;
  const { width, height, fps } = useVideoConfig();
  const { editData, segments, track, videoSrc } = useReels();
  const lag = useVideoLag();
  const cam = editData.camera;

  let S = 1;
  let tx = 0;
  let ty = 0;
  if (cam.enabled) {
    // which cut segment is this frame in?
    const segs =
      segments?.segments && segments.segments.length ? segments.segments : [{ start: 0, dur: editData.durationSec }];
    let idx = 0;
    // -1: OffthreadVideo draws the source frame at or before frame/fps, which on an
    // exact boundary lands a frame late. Without this the hard zoom steps one frame
    // BEFORE the picture cuts (same lag CustomGraphics compensates with VIDEO_LAG).
    for (let i = 0; i < segs.length; i++) {
      if (frame - lag >= Math.round(segs[i].start * fps)) idx = i;
    }
    const segFrom = Math.round(segs[idx].start * fps) + lag;
    const segLen = Math.max(1, Math.round(segs[idx].dur * fps));
    const base = cam.zooms[idx % cam.zooms.length] ?? 1.14;
    const push = cam.pushIn * clamp01((frame - segFrom) / segLen);
    S = base + push;

    const pts = (cam.tracking !== false && track?.points ? track.points : []) as [number, number][];
    const [cx, cy] = pts[Math.min(frame, pts.length - 1)] ?? [0.5, 0.4];
    tx = cam.targetX * width - cx * width * S;
    ty = cam.targetY * height - cy * height * S;
    tx = clamp(tx, width - width * S, 0); // never reveal an edge
    ty = clamp(ty, height - height * S, 0);
  }

  return (
    <AbsoluteFill>
      <div
        style={{
          width,
          height,
          transformOrigin: '0 0',
          transform: `translate(${tx.toFixed(2)}px, ${ty.toFixed(2)}px) scale(${S.toFixed(4)})`,
        }}
      >
        <OffthreadVideo src={src ?? videoSrc} transparent={transparent} style={{ width, height }} />
        {children}
      </div>
    </AbsoluteFill>
  );
};

// ============ BEHIND-THE-SUBJECT (element between person and background) ========
// Layer: base cut (bg+person) → element → person matte on top (person redrawn,
// so the element sits behind it). The matte is a ProRes 4444 alpha .mov from
// person_matte.py (aqui: POST /matte do align-worker), one file per window,
// frame 0 = window start. Elements anchor to the TOP of the frame (a centered
// element hides behind the torso). Constantes idênticas ao edvid.
//
// Prévia (@remotion/player): o Chrome não toca ProRes num <video>, e o
// OffthreadVideo vira <video> no Player. Lá o matte usa `mattePreview` (WebM VP9
// com alfa, mesmos quadros); sem ele, o elemento aparece sem a pessoa por cima.
type BehindLayer = BehindItem & { mattePreview?: string };

const BehindImageEl: React.FC<{ src: string; totalFrames: number }> = ({ src, totalFrames }) => {
  const f = useCurrentFrame();
  const enter = interpolate(f, [0, 9], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: Easing.out(Easing.cubic) });
  const exit = interpolate(f, [totalFrames - 8, totalFrames], [1, 0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const op = Math.min(enter, exit);
  const grow = interpolate(f, [0, totalFrames], [1, 1.08], { extrapolateRight: 'clamp' });
  const scale = interpolate(enter, [0, 1], [0.94, 1]) * grow;
  return (
    <AbsoluteFill style={{ justifyContent: 'flex-start', alignItems: 'center' }}>
      <Sfx src="whoosh.mp3" />
      {/* top-weighted so the image frames the head instead of hiding behind the torso */}
      <div style={{ width: 1000, height: 1250, marginTop: 40, borderRadius: 30, overflow: 'hidden', opacity: op, scale: String(scale), boxShadow: '0 24px 70px rgba(0,0,0,0.55)' }}>
        <Img src={src} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
    </AbsoluteFill>
  );
};

const BehindWordsEl: React.FC<{ words: { t: string; at: number }[]; startSec: number; totalFrames: number }> = ({ words, startSec, totalFrames }) => {
  const f = useCurrentFrame();
  const { fps } = useVideoConfig();
  const scrim = interpolate(f, [0, 8, totalFrames - 8, totalFrames], [0, 1, 1, 0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  return (
    <AbsoluteFill style={{ justifyContent: 'flex-start', alignItems: 'center', paddingTop: 180 }}>
      <AbsoluteFill style={{ background: 'rgba(0,0,0,0.26)', opacity: scrim }} />
      {words.map((w, i) => {
        const from = Math.round((w.at - startSec) * fps);
        const to = i + 1 < words.length ? Math.round((words[i + 1].at - startSec) * fps) : totalFrames;
        if (f < from || f >= to) return null;
        const local = f - from;
        const pop = interpolate(local, [0, 6], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: Easing.out(Easing.back(1.7)) });
        const op = interpolate(local, [0, 4], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
        return (
          <div key={i} style={{ position: 'absolute', fontFamily, fontWeight: 900, fontSize: 360, color: '#fff', opacity: op, scale: String(0.72 + 0.28 * pop), letterSpacing: -12, textShadow: '0 6px 30px rgba(0,0,0,0.5)' }}>
            {w.t}
          </div>
        );
      })}
    </AbsoluteFill>
  );
};

const BehindSubject: React.FC = () => {
  const { fps } = useVideoConfig();
  const { editData } = useReels();
  const inPlayer = getRemotionEnvironment().isPlayer;
  return (
    <>
      {((editData.behind || []) as BehindLayer[]).map((b, i) => {
        const from = Math.round(b.start * fps);
        const duration = Math.round(b.dur * fps);
        if (duration <= 0) return null;
        // o matte ganha a MESMA câmera (frameOffset = quadro global) — senão a pessoa desliza
        const matte = inPlayer ? b.mattePreview : b.matte;
        return (
          <Sequence key={i} from={from} durationInFrames={duration} layout="none">
            {b.kind === 'image' ? (
              <BehindImageEl src={b.src} totalFrames={duration} />
            ) : (
              <BehindWordsEl words={b.words} startSec={b.start} totalFrames={duration} />
            )}
            {matte ? <DynamicVideo src={matte} frameOffset={from} transparent /> : null}
          </Sequence>
        );
      })}
    </>
  );
};

// ============ KARAOKE CAPTIONS (1 line, ≤3 words, rise up, safe-margin fit) =====
const cleanW = (t: string) => t.replace(/[.,!?…]+$/, '');
const isBreak = (t: string) => /[.,!?…]$/.test(t);

function buildLines(caps: Caption[], maxWords: number): Caption[][] {
  const lines: Caption[][] = [];
  let cur: Caption[] = [];
  for (const w of caps) {
    cur.push(w);
    if (cur.length >= maxWords || isBreak(w.text)) {
      lines.push(cur);
      cur = [];
    }
  }
  if (cur.length) lines.push(cur);
  return lines;
}

const Word: React.FC<{ caption: Caption; lineFromFrame: number }> = ({ caption, lineFromFrame }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const startLocal = (caption.startMs / 1000) * fps - lineFromFrame;
  // REGRA DA CAPA: o que começa no frame 0 entra PRONTO. O frame 0 é a capa do
  // vídeo (feed/thumbnail) e uma animação de entrada ali devolve um quadro
  // vazio. Só vale para quem começa em 0 — o resto do vídeo anima normalmente.
  const p =
    caption.startMs <= 0
      ? 1
      : interpolate(frame, [startLocal, startLocal + 7], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
          easing: Easing.out(Easing.cubic),
        });
  return (
    <span
      style={{
        display: 'inline-block',
        opacity: p,
        translate: `0px ${interpolate(p, [0, 1], [34, 0])}px`,
        marginRight: 18,
      }}
    >
      {cleanW(caption.text)}
    </span>
  );
};

// captions.windows lets the caption sit somewhere else for part of the video —
// resolved PER FRAME, not per line.
const CaptionShell: React.FC<{ fromFrame: number; children: React.ReactNode }> = ({ fromFrame, children }) => {
  const { fps } = useVideoConfig();
  const local = useCurrentFrame();
  const C = useReels().editData.captions;
  // Compared in FRAMES, never seconds. +1 is the same video lag the split layout
  // compensates for (see VIDEO_LAG in CustomGraphics).
  const lag = useVideoLag();
  const f = fromFrame + local;
  const w = (C.windows || []).find((x) => f >= Math.round(x.start * fps) + lag && f < Math.round(x.end * fps) + lag);
  return (
    <AbsoluteFill style={{ justifyContent: 'flex-end', alignItems: 'center', paddingBottom: w ? w.paddingBottom : C.paddingBottom }}>
      {children}
    </AbsoluteFill>
  );
};

const Karaoke: React.FC = () => {
  const { fps, durationInFrames } = useVideoConfig();
  const { editData, captions } = useReels();
  const C = editData.captions;
  const LINES = useMemo(() => buildLines(captions as Caption[], C.maxWords), [captions, C.maxWords]);
  return (
    <>
      {LINES.map((line, i) => {
        const from = Math.round((line[0].startMs / 1000) * fps);
        const nextFrom = i + 1 < LINES.length ? Math.round((LINES[i + 1][0].startMs / 1000) * fps) : durationInFrames;
        const duration = Math.max(1, nextFrom - from);
        const lineText = line.map((w) => cleanW(w.text)).join(' ');
        const { width } = measureText({
          text: lineText,
          fontFamily,
          fontSize: C.fontSize,
          fontWeight: 900,
          letterSpacing: '-1px',
        });
        // safe-margin fit: scale down so the line clears the platform action rail
        const fit = Math.min(1, C.safeWidth / width);
        return (
          <Sequence key={i} from={from} durationInFrames={duration} layout="none">
            <CaptionShell fromFrame={from}>
              <div
                style={{
                  fontFamily,
                  fontWeight: 900,
                  fontSize: C.fontSize,
                  color: 'white',
                  lineHeight: 1,
                  letterSpacing: -1,
                  whiteSpace: 'nowrap',
                  scale: String(fit),
                  textShadow: '0 4px 20px rgba(0,0,0,0.55)',
                }}
              >
                {line.map((w, j) => (
                  <Word key={j} caption={w} lineFromFrame={from} />
                ))}
              </div>
            </CaptionShell>
          </Sequence>
        );
      })}
    </>
  );
};

// ============ ILLUSTRATIVE IMAGE INSERTS (rounded card + shadow, upper zone) ====
const CARD_W = 780;
const CARD_H = 500;
const CARD_TOP = 90;

const InsertCard: React.FC<{ src: string; totalFrames: number }> = ({ src, totalFrames }) => {
  const frame = useCurrentFrame();
  const enter = interpolate(frame, [0, 9], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: Easing.out(Easing.cubic) });
  const exit = interpolate(frame, [totalFrames - 7, totalFrames], [1, 0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const opacity = Math.min(enter, exit);
  // dynamic zoom: the image itself grows slowly while on screen (Ken-Burns)
  const grow = interpolate(frame, [0, totalFrames], [1, 1.08], { extrapolateRight: 'clamp' });
  const scale = interpolate(enter, [0, 1], [0.92, 1]) * grow;
  const y = interpolate(enter, [0, 1], [26, 0]);
  return (
    <AbsoluteFill style={{ justifyContent: 'flex-start', alignItems: 'center' }}>
      <Sfx src="whoosh.mp3" />
      <div
        style={{
          width: CARD_W,
          height: CARD_H,
          marginTop: CARD_TOP,
          borderRadius: 28,
          overflow: 'hidden',
          opacity,
          scale: String(scale),
          translate: `0px ${y}px`,
          boxShadow: '0 18px 50px rgba(0,0,0,0.45)',
        }}
      >
        <Img src={src} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
    </AbsoluteFill>
  );
};

const Inserts: React.FC = () => {
  const { fps } = useVideoConfig();
  const { editData } = useReels();
  return (
    <>
      {(editData.inserts || []).map((it, i) => {
        const from = Math.round(it.start * fps);
        const duration = Math.round((it.end - it.start) * fps);
        if (duration <= 0) return null;
        return (
          <Sequence key={i} from={from} durationInFrames={duration} layout="none">
            <InsertCard src={it.src} totalFrames={duration} />
          </Sequence>
        );
      })}
    </>
  );
};

// ============ SOUNDTRACK — background bed ======================================
const Soundtrack: React.FC = () => {
  const { durationInFrames } = useVideoConfig();
  const { editData, assetBase } = useReels();
  const S = editData.soundtrack;
  return (
    <Audio
      src={assetUrl(assetBase, S.file)}
      volume={(f) =>
        interpolate(f, [0, 10, durationInFrames - 24, durationInFrames], [0, S.volume, S.volume, 0], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        })
      }
    />
  );
};

// ============ VISUAL HOOK (static headline in the first ~4s) ===================
// Four styles via `hook.style`, ALL of them two lines with the size fitted to the text:
//   "outline" (default): white + thick black stroke, no card, sentence-case.
//   "card": Poppins Black on a dark-gray rounded card, UPPERCASE, optional logo row.
//   "realce": each line on its own solid ACCENT marker block.
//   "misto": line 1 light white, line 2 heavy ACCENT.
// fontSizePx / maxFontPx are the CEILING for the fit — NOT a fixed size.
const HL_MIN = 28;
const DEFAULT_ACCENT = '#ff5200';

type HlStyle = { weights: [number, number]; cap: number; safeW: number; lh: number; top: number };
const HL_STYLES: Record<string, HlStyle> = {
  outline: { weights: [800, 800], cap: 51, safeW: 900, lh: 1.02, top: 330 },
  card: { weights: [900, 900], cap: 46, safeW: 820, lh: 1.06, top: 120 },
  realce: { weights: [900, 900], cap: 48, safeW: 830, lh: 1.04, top: 300 },
  misto: { weights: [400, 900], cap: 55, safeW: 900, lh: 0.98, top: 300 },
};

const hlWidth = (text: string, size: number, weight: number) =>
  text ? measureText({ text, fontFamily, fontSize: size, fontWeight: weight, letterSpacing: '-1px' }).width : 0;

// Balance by MEASURED width, not word count.
export function twoLines(text: string, weights: [number, number]): [string, string] {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return [words[0] ?? '', ''];
  let best: [string, string] = [words[0], words.slice(1).join(' ')];
  let bestDiff = Infinity;
  for (let i = 1; i < words.length; i++) {
    const a = words.slice(0, i).join(' ');
    const b = words.slice(i).join(' ');
    const d = Math.abs(hlWidth(a, 100, weights[0]) - hlWidth(b, 100, weights[1]));
    if (d < bestDiff) {
      bestDiff = d;
      best = [a, b];
    }
  }
  return best;
}

// Width scales with size, but letterSpacing (-1px per gap) does NOT — one
// refinement pass at the estimated size fixes that.
export function fitHeadline(lines: [string, string], s: HlStyle): number {
  const widest = (size: number) => Math.max(hlWidth(lines[0], size, s.weights[0]), hlWidth(lines[1], size, s.weights[1]));
  let size = Math.floor((s.safeW / Math.max(1, widest(100))) * 100);
  size = clamp(Math.floor((s.safeW / Math.max(1, widest(size))) * size), HL_MIN, s.cap);
  return size;
}

// ─── "empilhado": 3 linhas com fontes diferentes, maior que as outras headlines ───
// Mesma família visual da legenda stacked (Poppins itálico forte / Poppins fino /
// Playfair itálico no destaque), mas ESTÁTICA e inteira no frame 0 como toda
// headline. Linha 3 é o soco: serifada maior na cor de destaque.
const STACK_HL = { top: 250, safeW: 940, cap1: 118, ratio2: 0.6, ratio3: 1.22, cap3: 150, min: 30 };

const measure = (text: string, family: string, size: number, weight: number, italic = false) =>
  text ? measureText({ text, fontFamily: family, fontSize: size, fontWeight: weight, fontStyle: italic ? 'italic' : 'normal', letterSpacing: '-1px' } as any).width : 0;

/** Sem as 3 partes: o soco são as últimas 1–2 palavras; o resto divide em duas linhas por largura. */
export function threeParts(text: string): [string, string, string] {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length <= 2) return [words[0] ?? '', '', words[1] ?? ''];
  const punchN = words.length >= 6 ? 2 : 1;
  const punch = words.slice(-punchN).join(' ');
  const rest = words.slice(0, -punchN);
  if (rest.length === 1) return [rest[0], '', punch];
  const [a, b] = twoLines(rest.join(' '), [900, 400]);
  return [a, b, punch];
}

function isDark(hex: string): boolean {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return false;
  const n = parseInt(m[1], 16);
  const lum = (0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)) / 255;
  return lum < 0.35;
}

const StackedHeadline: React.FC<{ op: number; accent: string }> = ({ op, accent }) => {
  const H = useReels().editData.hook;
  const given = (H.parts || []).map((p) => String(p || '').trim());
  const [l1, l2, l3] = given.filter(Boolean).length ? [given[0] || '', given[1] || '', given[2] || ''] : threeParts((H.text ?? (H.lines || []).join(' ')).trim());
  const safeW = H.safeWidth ?? STACK_HL.safeW;
  const fit = (text: string, family: string, weight: number, italic: boolean, cap: number) => {
    if (!text) return cap;
    const w = measure(text, family, 100, weight, italic);
    return clamp(Math.floor((safeW / Math.max(1, w)) * 100), STACK_HL.min, cap);
  };
  const s1 = fit(l1, fontFamily, 900, true, H.fontSizePx ?? H.maxFontPx ?? STACK_HL.cap1);
  const s2 = fit(l2, fontFamily, 400, false, Math.round(s1 * STACK_HL.ratio2));
  const s3 = fit(l3, PLAYFAIR, 900, true, Math.min(STACK_HL.cap3, Math.round(s1 * STACK_HL.ratio3)));
  const glow = isDark(accent) ? '0 0 22px rgba(255,255,255,0.35), 0 4px 14px rgba(255,255,255,0.18)' : '0 6px 20px rgba(0,0,0,0.5)';
  const line: React.CSSProperties = { whiteSpace: 'nowrap', lineHeight: 0.98, letterSpacing: -1 };
  return (
    <AbsoluteFill style={{ justifyContent: 'flex-start', alignItems: 'center', paddingTop: H.paddingTop ?? STACK_HL.top }}>
      <Sfx src="whoosh.mp3" volume={0.1} />
      <div style={{ opacity: op, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        {l1 ? (
          <div
            style={{
              ...line,
              fontFamily,
              fontWeight: 900,
              fontStyle: 'italic',
              fontSize: s1,
              color: '#fff',
              backgroundImage: 'linear-gradient(180deg,#ffffff 0%,#ffffff 55%,#dcdcdc 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              filter: 'drop-shadow(0 6px 16px rgba(0,0,0,0.5))',
            }}
          >
            {l1}
          </div>
        ) : null}
        {l2 ? (
          <div style={{ ...line, fontFamily, fontWeight: 400, fontSize: s2, color: '#fff', marginTop: '0.04em', textShadow: '0 4px 14px rgba(0,0,0,0.55)' }}>{l2}</div>
        ) : null}
        {l3 ? (
          <div style={{ ...line, fontFamily: PLAYFAIR, fontWeight: 900, fontStyle: 'italic', fontSize: s3, color: accent, marginTop: '-0.06em', textShadow: glow }}>{l3}</div>
        ) : null}
      </div>
    </AbsoluteFill>
  );
};

// ─── "pergunta": a caixinha de perguntas do fluxo antigo ───
// Mesma geometria do marketingStudioOverlayService.renderQuestionBoxImage (largura
// 0,56 do quadro, topo a 7%, tarja #1C1C1E com o título, cartão branco com a
// pergunta), agora como headline: entra inteira no frame 0.
const QB = { widthRatio: 0.56, topRatio: 0.07, padRatio: 0.04, titleRatio: 0.056, textRatio: 0.07, lh: 1.12 };

const QuestionHeadline: React.FC<{ op: number }> = ({ op }) => {
  const { width, height } = useVideoConfig();
  const H = useReels().editData.hook;
  const title = (H.question?.title || 'O que você me perguntaria?').trim();
  const text = (H.question?.text || H.text || (H.lines || []).join(' ')).trim();
  const boxW = Math.round(width * QB.widthRatio);
  const pad = Math.round(boxW * QB.padRatio);
  const titleSize = Math.round(boxW * QB.titleRatio);
  const textSize = Math.round(boxW * QB.textRatio);
  const radius = Math.round(boxW * 0.035);
  return (
    <AbsoluteFill style={{ justifyContent: 'flex-start', alignItems: 'center', paddingTop: H.paddingTop ?? Math.round(height * QB.topRatio) }}>
      <Sfx src="pop.mp3" volume={0.12} />
      <div style={{ opacity: op, width: boxW, borderRadius: radius, overflow: 'hidden', boxShadow: '0 18px 44px rgba(0,0,0,0.35)', fontFamily, textAlign: 'center' }}>
        <div style={{ background: '#1C1C1E', color: '#fff', fontWeight: 600, fontSize: titleSize, lineHeight: QB.lh, padding: pad }}>{title}</div>
        <div style={{ background: '#fff', color: '#1C1C1E', fontWeight: 500, fontSize: textSize, lineHeight: QB.lh, padding: `${Math.round(pad * 0.8)}px ${pad}px ${Math.round(pad * 1.25)}px` }}>{text}</div>
      </div>
    </AbsoluteFill>
  );
};

const HookInner: React.FC<{ totalFrames: number }> = ({ totalFrames }) => {
  const f = useCurrentFrame();
  const H = useReels().editData.hook;
  // SEM animação de entrada, de propósito: o frame 0 é a CAPA do vídeo. O hook
  // entra pronto no segundo 0; só a SAÍDA continua suave.
  const exit = interpolate(f, [totalFrames - 9, totalFrames], [1, 0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const op = exit;
  const y = 0;

  const styleId = H.style ?? 'outline';
  if (styleId === 'empilhado') return <StackedHeadline op={op} accent={H.accent || DEFAULT_ACCENT} />;
  if (styleId === 'pergunta') return <QuestionHeadline op={op} />;
  const S = HL_STYLES[styleId] ?? HL_STYLES.outline;
  const accent = H.accent || DEFAULT_ACCENT;
  const raw = (H.text ?? (H.lines || []).join(' ')).trim();
  const lines = twoLines(styleId === 'card' ? raw.toUpperCase() : raw, S.weights);
  const cap = H.fontSizePx ?? H.maxFontPx ?? S.cap;
  const size = fitHeadline(lines, { ...S, cap, safeW: H.safeWidth ?? S.safeW });
  const lh = H.lineHeight ?? S.lh;
  const top = H.paddingTop ?? S.top;
  const shell: React.CSSProperties = {
    opacity: op,
    translate: `0px ${y}px`,
    textAlign: 'center',
    fontFamily,
    lineHeight: lh,
    letterSpacing: -1,
    // the two-line promise is structural: overflow visibly instead of wrapping
    whiteSpace: 'nowrap',
  };

  if (styleId === 'realce') {
    return (
      <AbsoluteFill style={{ justifyContent: 'flex-start', alignItems: 'center', paddingTop: top }}>
        <Sfx src="whoosh.mp3" volume={0.1} />
        <div style={{ ...shell, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          {lines.filter(Boolean).map((l, i) => (
            <div
              key={i}
              style={{
                background: accent,
                color: '#fff',
                fontWeight: 900,
                fontSize: size,
                padding: '0.08em 0.3em 0.16em',
                borderRadius: 12,
                boxShadow: '0 10px 28px rgba(0,0,0,0.45)',
              }}
            >
              {l}
            </div>
          ))}
        </div>
      </AbsoluteFill>
    );
  }

  if (styleId === 'misto') {
    return (
      <AbsoluteFill style={{ justifyContent: 'flex-start', alignItems: 'center', paddingTop: top }}>
        <Sfx src="whoosh.mp3" volume={0.1} />
        <div style={{ ...shell, filter: 'drop-shadow(0 6px 16px rgba(0,0,0,0.55))' }}>
          <div style={{ fontWeight: 400, fontSize: size, color: '#fff' }}>{lines[0]}</div>
          <div style={{ fontWeight: 900, fontSize: size, color: accent }}>{lines[1]}</div>
        </div>
      </AbsoluteFill>
    );
  }

  if (styleId === 'card') {
    return (
      <AbsoluteFill style={{ justifyContent: 'flex-start', alignItems: 'center', paddingTop: top }}>
        <Sfx src="whoosh.mp3" volume={0.1} />
        <div style={{ opacity: op, translate: `0px ${y}px`, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 28 }}>
          {H.logo || H.sign ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 34 }}>
              {H.logo ? <Img src={H.logo} style={{ width: 300, borderRadius: 18, boxShadow: '0 12px 34px rgba(0,0,0,0.4)' }} /> : null}
              {H.sign ? <Img src={H.sign} style={{ width: 128, filter: 'drop-shadow(0 8px 20px rgba(0,0,0,0.45))' }} /> : null}
            </div>
          ) : null}
          <div
            style={{
              background: '#232326',
              borderRadius: 24,
              padding: '28px 46px',
              textAlign: 'center',
              fontFamily,
              fontWeight: 900,
              fontSize: size,
              color: '#fff',
              lineHeight: lh,
              letterSpacing: -1,
              textShadow: '0 4px 20px rgba(0,0,0,0.55)',
              boxShadow: '0 18px 50px rgba(0,0,0,0.45)',
            }}
          >
            {lines.filter(Boolean).map((l, i) => (
              <div key={i}>{l}</div>
            ))}
          </div>
        </div>
      </AbsoluteFill>
    );
  }

  const stroke = H.strokePx ?? 7;
  return (
    <AbsoluteFill style={{ justifyContent: 'flex-start', alignItems: 'center', paddingTop: top }}>
      <Sfx src="whoosh.mp3" volume={0.1} />
      <div
        style={{
          ...shell,
          fontWeight: 800,
          fontSize: size,
          color: '#fff',
          WebkitTextStroke: `${stroke}px #000`,
          paintOrder: 'stroke fill',
          filter: 'drop-shadow(0 6px 14px rgba(0,0,0,0.45))',
          padding: '0 60px',
        }}
      >
        {lines.filter(Boolean).map((l, i) => (
          <div key={i}>{l}</div>
        ))}
      </div>
    </AbsoluteFill>
  );
};

const HookIntro: React.FC = () => {
  const { fps } = useVideoConfig();
  const { editData } = useReels();
  const dur = Math.round(editData.hook.endSec * fps);
  if (dur <= 0) return null;
  return (
    <Sequence from={0} durationInFrames={dur} layout="none">
      <HookInner totalFrames={dur} />
    </Sequence>
  );
};

const Captions: React.FC = () => {
  const style = useReels().editData.captions.style;
  if (style === 'stacked') return <StackedCaptions />;
  if (style === 'scatter') return <ScatterCaptions />;
  if (style && SIMPLE_VARIANTS[style]) return <SimpleCaptions variant={style} />;
  return <Karaoke />;
};

// ============ MAIN ============
const MainInner: React.FC = () => {
  const { editData, assetBase } = useReels();
  const fontsReady = useFontsReady(assetBase);
  const H0 = editData.hook;
  const hasHookText = Boolean(
    (H0.text ?? (H0.lines || []).join(' ')).trim() || (H0.parts || []).some((p) => String(p || '').trim()) || (H0.question?.text || '').trim(),
  );
  return (
    <AbsoluteFill style={{ backgroundColor: 'black' }}>
      {editData.soundtrack?.enabled && editData.soundtrack.file ? <Soundtrack /> : null}
      <DynamicVideo />
      <BehindSubject />
      <Inserts />
      <CustomGraphics />
      {fontsReady && editData.hook.enabled && hasHookText ? <HookIntro /> : null}
      {fontsReady && editData.captions.enabled ? <Captions /> : null}
    </AbsoluteFill>
  );
};

/** Composição "Reels": o MESMO componente no render-worker e no @remotion/player. */
export const Main: React.FC<ReelsProps> = (props) => {
  const value = useMemo<ReelsData>(
    () => ({ ...props, assetBase: props.assetBase || DEFAULT_ASSET_BASE }),
    [props],
  );
  return (
    <ReelsProvider value={value}>
      <MainInner />
    </ReelsProvider>
  );
};
